import React, { useCallback, useState } from 'react';
import classNames from 'classnames';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { readBool } from 'utils/readBool';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import {
  deleteKit, deleteKitLine, patchKit, patchKitLine,
} from 'containers/KitWorkspaceStorage/api';
import { MAX_LINES_PER_KIT } from 'containers/KitWorkspaceStorage/types';
import type { IKit, IKitLine } from 'containers/KitWorkspaceStorage/types';
import { buildSpecSummary } from 'common/components/kit-shared/assemblySummary';
import { normalizeQuery, assemblyMatches } from 'common/components/kit-shared/kitFilter';
import AssemblyName from 'common/components/kit-shared/AssemblyName';

import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import QtyStepper from 'common/components/kit-shared/QtyStepper';
import KitFormDialog from 'common/components/kit-shared/KitFormDialog';
import AssemblyFormDialog from 'common/components/kit-shared/AssemblyFormDialog';
import ConfirmDialog from 'common/components/kit-shared/ConfirmDialog';
import CompanionsCatalog from 'common/components/CompanionsCatalog';
import AccessoryLines from './AccessoryLines';
import styles from './styles.module.scss';

interface IKitCardProps {
  kit: IKit;
  // Active Manage Kits search; when set, the card isolates the matching lines and
  // parts (and stays expanded) so a part/assembly search shows just the hit.
  query?: string;
}

const KitLineRow = ({ line }: { line: IKitLine }) => {
  const t = useKitTranslate();
  const { workspaceKey, refetch } = useKitWorkspace();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const spec = buildSpecSummary(line.assembly);

  const onQty = useCallback(async (qty: number) => {
    setBusy(true);
    try {
      await patchKitLine(line.uuid, { qty });
      track('kit_line_qty_changed', { workspaceKey: workspaceKey ?? undefined, kitLineId: line.uuid, qty });
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
    } finally {
      setBusy(false);
    }
  }, [line.uuid, workspaceKey, refetch, t]);

  const onRemove = useCallback(async () => {
    setBusy(true);
    try {
      await deleteKitLine(line.uuid);
      showToast(ToastTypes.success, t('texts.kits.toast.lineRemoved'));
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
    } finally {
      setBusy(false);
    }
  }, [line.uuid, refetch, t]);

  return (
    <li className={styles.line}>
      <div className={styles.lineInfo}>
        <AssemblyName assembly={line.assembly} nameClassName={styles.lineName} hideRecommended />
        {spec && <span className={styles.lineSub}>{spec}</span>}
      </div>
      <div className={styles.lineActions}>
        <QtyStepper value={line.qty} onChange={onQty} disabled={busy} />
        <button
          type="button"
          className={styles.editLineBtn}
          onClick={() => setEditing(true)}
          disabled={busy}
          aria-label={t('texts.kits.edit')}
          title={t('texts.kits.edit')}
        >
          <svg
            className={styles.editLineIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
        <button
          type="button"
          className={styles.removeBtn}
          onClick={onRemove}
          disabled={busy}
          aria-label={t('texts.kits.removeLine')}
        >
          &#x2715;
        </button>
      </div>

      {editing && (
        <AssemblyFormDialog
          assembly={line.assembly}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
    </li>
  );
};

/**
 * Collapsible kit card: name/notes, counts, untitled warning, edit/delete,
 * Add assemblies (kit lines + qty + remove), Add parts (companions),
 * accessory lines. Enforces the 20-line cap (disables Add + shows a warning).
 */
const KitCard = ({ kit, query = '' }: IKitCardProps) => {
  const t = useKitTranslate();
  const { workspaceKey, refetch } = useKitWorkspace();

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);

  const lineCount = kit.lines.length;
  const looseLines = kit.accessories.length;
  const atCap = lineCount >= MAX_LINES_PER_KIT;

  // The companions catalog runs off a per-account rule-set. Where the account
  // has none the picker can only ever come up empty, so hide the entry point
  // rather than open it and ask the server for a list that is not there.
  const addPartsEnabled = readBool('variables.showAddParts');

  // With an active search, show only the lines/parts that match (a kit matched by
  // its own name shows everything), and keep the body open so the hit is visible.
  const q = normalizeQuery(query);
  const nameMatch = !!q && [kit.name, kit.notes].filter(Boolean).join(' ').toLowerCase().includes(q);
  const showAll = !q || nameMatch;
  const visibleLines = showAll ? kit.lines : kit.lines.filter((l) => assemblyMatches(l.assembly, q));
  const visibleAccessories = showAll
    ? kit.accessories
    : kit.accessories.filter((a) => `${a.partNumber} ${a.description ?? ''}`.toLowerCase().includes(q));
  const expanded = !kit.collapsed || !!q;

  const toggleCollapse = useCallback(async () => {
    try {
      await patchKit(kit.uuid, { collapsed: !kit.collapsed });
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
    }
  }, [kit.uuid, kit.collapsed, refetch, t]);

  const confirmDelete = useCallback(async () => {
    setBusy(true);
    try {
      await deleteKit(kit.uuid);
      showToast(ToastTypes.success, t('texts.kits.toast.kitDeleted'));
      // Kit disappears from the list on refetch, unmounting this card.
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
      setBusy(false);
    }
  }, [kit.uuid, refetch, t]);

  return (
    <div className={styles.kitCard}>
      <div className={styles.kitHeader}>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={toggleCollapse}
          aria-label={kit.collapsed ? t('texts.kits.expand') : t('texts.kits.collapse')}
          aria-expanded={expanded}
        >
          {expanded ? '▾' : '▸'}
        </button>
        <div className={styles.kitTitleBlock}>
          <span className={styles.kitName}>
            {kit.name || <span className={styles.pill}>{t('texts.kits.pills.untitled')}</span>}
          </span>
        </div>
        <span className={styles.kitCountPill}>
          {t('texts.kits.counts', { count: lineCount + looseLines })}
        </span>
        <div className={styles.kitHeaderActions}>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => setShowEdit(true)}
          >
            <svg
              className={styles.actionIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            {t('texts.kits.edit')}
          </button>
          <button
            type="button"
            className={classNames(styles.actionBtn, styles.actionBtnDanger)}
            onClick={() => setShowDelete(true)}
            disabled={busy}
          >
            <svg
              className={styles.actionIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6" />
            </svg>
            {t('texts.kits.delete')}
          </button>
        </div>
      </div>

      {expanded && (
        <div className={styles.kitBody}>
          {atCap && (
            <p className={styles.warningBanner}>{t('texts.kits.warnings.lineCap', { limit: MAX_LINES_PER_KIT })}</p>
          )}

          {visibleLines.length > 0 && (
            <ul className={styles.lineList}>
              {visibleLines.map((line) => <KitLineRow key={line.uuid} line={line} />)}
            </ul>
          )}

          {visibleAccessories.length > 0 && (
            <>
              <div className={styles.subHeader}>{t('texts.kits.partsHeader')}</div>
              <AccessoryLines accessories={visibleAccessories} />
            </>
          )}

          {addPartsEnabled && (
            <div className={styles.kitActions}>
              <Button
                schema={ButtonSchema.secondary}
                size={ButtonSize.small}
                className={styles.addPartsBtn}
                onClick={() => setShowCatalog(true)}
              >
                {`+ ${t('texts.kits.addParts')}`}
              </Button>
            </div>
          )}
        </div>
      )}

      {showEdit && (
        <KitFormDialog
          workspaceKey={workspaceKey ?? ''}
          kit={kit}
          onCancel={() => setShowEdit(false)}
          onSaved={() => setShowEdit(false)}
        />
      )}

      {showDelete && (
        <ConfirmDialog
          title={t('texts.kits.deleteKit.title')}
          message={t('texts.kits.deleteKit.question', { name: kit.name || t('texts.kits.pills.untitled') })}
          note={t('texts.kits.deleteKit.note', { count: lineCount + looseLines })}
          confirmLabel={t('texts.kits.deleteKit.confirmBtn')}
          danger
          busy={busy}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {showCatalog && (
        <CompanionsCatalog
          target={{ kitUuid: kit.uuid }}
          onClose={() => setShowCatalog(false)}
        />
      )}
    </div>
  );
};

export default KitCard;
